import { Portal } from 'bold-ui'
import { useSession } from 'components/auth/useSession'
import { CIDADAO_PATH } from 'components/layout/SideMenu/SideMenuItems'
import { usePopperButton } from 'hooks/usePopperButton'
import { useServerTime } from 'hooks/useServerTime'
import React, { Fragment, useState } from 'react'
import { useHistory } from 'react-router'
import { JustificativaCancelamentoAgendaComboEnum } from 'types/enums'
import { useAgendaAuthorization } from 'view/agenda/authorization/useAgendaAuthorization'
import { AgendamentoEdicaoView } from 'view/agenda/cadastro-agendamento/AgendamentoEdicaoView'
import { useAgendaContext } from 'view/agenda/hooks/agenda-context/useAgendaContext'

import { AcessarVideochamadaAgendamentoButton } from './buttons/AcessarVideochamadaAgendamentoButton'
import { CancelarAgendamentoButton } from './buttons/CancelarAgendamentoButton'
import { EditarAgendamentoButton } from './buttons/EditarAgendamentoButton'
import { ImprimirAgendamentoButton } from './buttons/ImprimirAgendamentoButton'
import { InformarFaltaCidadaoButton } from './buttons/InformarFaltaCidadaoButton'
import { InserirListaAtendimentoButton } from './buttons/InserirListaAtendimentoButton'
import { RemoverFaltaCidadaoButton } from './buttons/RemoverFaltaCidadaoButton'
import { RemoverListaAtendimentoButton } from './buttons/RemoverListaAtendimentoButton'
import { VisualizarInformacoesCidadaoButton } from './buttons/VisualizarInformacoesCidadaoButton'
import { VisualizarProntuarioButton } from './buttons/VisualizarProntuarioButton'
import CancelarAgendamentoModal from './CancelarAgendamentoModal'
import { getStatusAcoesConsulta } from './getStatusAcoesConsulta'
import { getStatusAcoesConsultaAD } from './getStatusAcoesConsultaAD'
import MenuAcoesAgendamento from './MenuAcoesAgendamento'
import { AcaoAgendamentoButtonProps, AcaoAgendamentoEnum, AcoesAgendamentoProps } from './model-acoesAgendamento'

export interface AcoesAgendamentoConsultaProps extends AcoesAgendamentoProps {
  isDropdown: boolean
}

export function AcoesAgendamentoConsulta(props: Readonly<AcoesAgendamentoConsultaProps>) {
  const { event, isDropdown } = props
  const { getServerTimeNow } = useServerTime()

  const history = useHistory()
  const { data: session } = useSession()

  const { hasAuthorizationAgendar, hasAuthorizationCancelar } = useAgendaAuthorization(event.isAtencaoDomiciliar)

  const hasAcessoVideochamada = event.videochamadaUuid && session.acesso?.id === event.lotacao?.id

  const [modalCancelamentoProps, setModalCancelamentoProps] = useState<{
    open: boolean
    justificativa?: JustificativaCancelamentoAgendaComboEnum
    justificativaOutra?: string
  }>({ open: false })
  const { refetchInfos } = useAgendaContext()

  const statusAcoes = event.isAtencaoDomiciliar
    ? getStatusAcoesConsultaAD({ dataConsulta: event.start, dataAtual: getServerTimeNow() })
    : getStatusAcoesConsulta({
        situacao: event.situacao,
        situacaoAtendimento: event.atendimento?.situacao,
        possuiAtendimentoPrevio: event.atendimento?.possuiAtendimentosProfissionais,
        isForaUbs: event.isForaUbs,
        dataAtual: getServerTimeNow(),
        dataConsulta: new Date(event.start),
      })
  const statusVisualizarProntuario = statusAcoes[AcaoAgendamentoEnum.VISUALIZAR_PRONTUARIO]
  const statusEditarAgendamento = statusAcoes[AcaoAgendamentoEnum.EDITAR_AGENDAMENTO]
  const statusCancelarAgendamento = statusAcoes[AcaoAgendamentoEnum.CANCELAR_AGENDAMENTO]
  const statusImprimirAgendamento = statusAcoes[AcaoAgendamentoEnum.IMPRIMIR_AGENDAMENTO]
  const isConsultaCompartilhada = !!event.nomeProfissionalParticipante

  const handleClickVisualizarProntuario = () => {
    history.push(`${CIDADAO_PATH}/${event.cidadaoId}/folha-rosto`)
  }

  const { rootRef, popperContainerRef, buttonRef, toggle, close, isOpen, popperContainerStyle } = usePopperButton({
    referenceElement: props.eventoElement,
    placement: 'bottom-start',
    strategy: 'absolute',
    closeOnBackdropClick: true,
  })

  return (
    <Fragment>
      <Portal>
        <div ref={rootRef}>
          {isOpen && (
            <div
              ref={popperContainerRef}
              className={popperContainerStyle}
              data-placement='bottom-start'
              data-visible={isOpen}
            >
              <AgendamentoEdicaoView {...props} onClose={close} isAtencaoDomiciliar={event.isAtencaoDomiciliar} />
            </div>
          )}
        </div>
      </Portal>
      <MenuAcoesAgendamento>
        {hasAcessoVideochamada && (
          <AcessarVideochamadaAgendamentoButton videochamadaUuid={event.videochamadaUuid} isDropdown={isDropdown} />
        )}
        {statusVisualizarProntuario.visible && (
          <VisualizarProntuarioButton
            {...props}
            disabled={statusVisualizarProntuario.disabled}
            tooltip={statusVisualizarProntuario.tooltip}
            isDropdown={!isConsultaCompartilhada || hasAcessoVideochamada}
            onClick={handleClickVisualizarProntuario}
          />
        )}
        <VisualizarInformacoesCidadaoButton
          cidadaoId={event.cidadaoId}
          isDropdown={isDropdown}
          disabled={statusVisualizarProntuario.disabled}
        />
        {Object.keys(AcaoAgendamentoEnum).map(
          (acao) =>
            statusAcoes[acao].visible &&
            recordBotoesAcoes[acao] &&
            React.createElement(recordBotoesAcoes[acao], {
              ...props,
              key: acao,
              disabled: statusAcoes[acao].disabled,
              tooltip: statusAcoes[acao].tooltip,
              isDropdown,
            })
        )}
        {hasAuthorizationAgendar(event.lotacao) && (
          <EditarAgendamentoButton
            {...props}
            buttonRef={buttonRef}
            disabled={statusEditarAgendamento.disabled}
            tooltip={statusEditarAgendamento.tooltip}
            onClick={toggle}
            isDropdown
          />
        )}
        <ImprimirAgendamentoButton
          {...props}
          disabled={statusImprimirAgendamento.disabled}
          tooltip={statusImprimirAgendamento.tooltip}
          isDropdown
        />
        {hasAuthorizationCancelar(event.lotacao) && (
          <CancelarAgendamentoButton
            {...props}
            openModalCancelarAgendamento={(justificativa, justificativaOutra) =>
              setModalCancelamentoProps({ open: true, justificativa, justificativaOutra })
            }
            disabled={statusCancelarAgendamento.disabled}
            tooltip={statusCancelarAgendamento.tooltip}
            isDropdown
          />
        )}
      </MenuAcoesAgendamento>
      <CancelarAgendamentoModal
        event={event}
        setOpen={(open) => setModalCancelamentoProps({ open })}
        onAction={refetchInfos}
        {...modalCancelamentoProps}
      />
    </Fragment>
  )
}

const recordBotoesAcoes: Partial<Record<AcaoAgendamentoEnum, React.ComponentType<AcaoAgendamentoButtonProps>>> = {
  INSERIR_LISTA_ATENDIMENTO: InserirListaAtendimentoButton,
  REMOVER_LISTA_ATENDIMENTO: RemoverListaAtendimentoButton,
  INFORMAR_FALTA_CIDADAO: InformarFaltaCidadaoButton,
  REMOVER_FALTA_CIDADAO: RemoverFaltaCidadaoButton,
}
